import { stardust } from '@eidellev/adonis-stardust/client';

import MultiStepForm from './multi-step-form';
import DualHandleRange from './dual-handle-range';
import { getPageLang } from './utils';
import { getCookie, setCookie } from './cookie-utils';

import i18next, { type TFunction } from 'i18next';
import i18nextInstance from './i18next';
let t: TFunction;

/**
 * Build Algolia search params from the form data
 * @param form The ring assistant form
 * @returns
 */
const buildAlgoliaOptions = (form: HTMLFormElement) => {
  const formData = new FormData(form);

  const options = {};

  // https://www.raffi-jewellers.ca/collections/fine-jewellery/rings?labGrown=true
  // (labGrown=true)
  const labGrown = formData.getAll('origin');
  // Algolia counts any value as a checkmark, even 'false'
  if (labGrown.includes('lab-grown')) {
    options['labGrown'] = true;
  }

  // https://www.raffi-jewellers.ca/collections/fine-jewellery/rings?gemstoneShape%5B0%5D=Round%20Brilliant
  // (gemstoneShape[0]=Round Brilliant)
  const gemstoneShape = formData.getAll('shape');
  gemstoneShape.forEach((shape, index) => {
    options[`gemstoneShape[${index}]`] = shape.toString();
  });

  // https://www.raffi-jewellers.ca/collections/engagement-rings?diamondSettings%5B0%5D=Halo
  // (diamondSettings[0]=Halo)
  const style = formData.getAll('style');
  style.forEach((style, index) => {
    options[`diamondSettings[${index}]`] = style.toString();
  });

  // Combined material and colour step
  // https://www.raffi-jewellers.ca/collections/fine-jewellery/rings?material%5B0%5D=18k%20Gold
  // (material[0]=18k Gold) - 14k Gold, 18k Gold, Platinum
  // https://www.raffi-jewellers.ca/collections/fine-jewellery/rings?metalColour%5B0%5D=White
  // (metalColour[0]=White) - White, Yellow, Rose
  const materialColour = formData.getAll('material-colour');
  // Add to a set, to remove duplicates
  const materialsSelected = new Set<string>();
  const metalColoursSelected = new Set<string>();
  materialColour.forEach((materialColour) => {
    switch (materialColour) {
      // NOTE: This is English in the form value so it can be read here
      // and we translate it to the French at this point in the code.
      case 'White Gold':
        materialsSelected.add(t('algolia:ringAssistant.steps.material.14kGold'));
        materialsSelected.add(t('algolia:ringAssistant.steps.material.18kGold'));
        metalColoursSelected.add(t('algolia:ringAssistant.steps.metalColour.white'));
        break;
      case 'Yellow Gold':
        materialsSelected.add(t('algolia:ringAssistant.steps.material.14kGold'));
        materialsSelected.add(t('algolia:ringAssistant.steps.material.18kGold'));
        metalColoursSelected.add(t('algolia:ringAssistant.steps.metalColour.yellow'));
        break;
      case 'Rose Gold':
        materialsSelected.add(t('algolia:ringAssistant.steps.material.14kGold'));
        materialsSelected.add(t('algolia:ringAssistant.steps.material.18kGold'));
        metalColoursSelected.add(t('algolia:ringAssistant.steps.metalColour.rose'));
        break;
      case 'Platinum':
        materialsSelected.add(t('algolia:ringAssistant.steps.material.platinum'));
        break;
    }
  });

  // Add to the options object
  Array.from(materialsSelected).forEach((material, index) => {
    options[`material[${index}]`] = material;
  });
  Array.from(metalColoursSelected).forEach((metalColour, index) => {
    options[`metalColour[${index}]`] = metalColour;
  });

  // https://www.raffi-jewellers.ca/collections/fine-jewellery/rings?diamondWeight=2%3A3
  // (diamondWeight=2:3)
  const weightMin = formData.get('weight_min') as string;
  const weightMax = formData.get('weight_max') as string;
  let weightString = '';
  if (weightMin !== null) {
    weightString = weightMin;
  }
  if (weightMax !== null) {
    weightString += `:${weightMax}`;
  }
  if (weightString !== '') {
    options['diamondWeight'] = weightString;
  }

  const priceMin = formData.get('budget_min') as string;
  const priceMax = formData.get('budget_max') as string;
  let priceString = '';
  if (priceMin !== null) {
    priceString = priceMin;
  }
  if (priceMax !== null) {
    priceString += `:${priceMax}`;
  }
  if (priceString !== '') {
    options['price'] = priceString;
  }

  return options;
};

const toggleNoResultsScreen = (show: boolean) => {
  const container = document.querySelector<HTMLFormElement>('#ring-assistant-form');
  if (!container) {
    console.error('Form not found');
    return;
  }

  // Hide the multi-step form elements
  const formHeader = container.querySelector<HTMLElement>('.form-header');
  const formFooter = container.querySelector<HTMLElement>('.form-footer');
  const formTitle = container.querySelector<HTMLElement>('.form__title');
  const formEyebrow = container.querySelector<HTMLElement>('.form__eyebrow');
  const formSteps = container.querySelectorAll('.step');
  formHeader?.classList.toggle('hidden-important', show);
  formFooter?.classList.toggle('hidden-important', show);
  formTitle?.classList.toggle('hidden-important', show);
  formEyebrow?.classList.toggle('hidden-important', show);
  formSteps.forEach((step) => {
    step.classList.toggle('hidden-important', show);
  });

  const noResultsScreen = document.querySelector<HTMLElement>('.ring-assistant__end-screen');
  noResultsScreen?.classList.toggle('hidden-important', !show);
};

const getLabelValueFromInputValue = (form: HTMLFormElement, name: string, value: string) => {
  const inputLabelSpan = form.querySelector<HTMLLabelElement>(`label[for="ring-assistant-${name}-${value}"] span`);
  return inputLabelSpan
    ? inputLabelSpan.innerText
    : value;
};

/** Render a single form selection (text column) */
const buildSelectionDisplayText = (selections: string[]) => {
  if (selections.length === 0) {
    selections.push(t('forms:ringAssistant.review.noSelection'));
  }

  const selectionList = selections.map((selection) => {
    return `<li>${selection}</li>`;
  });
  const html = '<ul>' + selectionList.join('') + '</ul>';
  return html;
};

type IconSelection = {
  name: string;
  icon: string;
};

/** Render a single form selection (icon column) */
const buildSelectionDisplayIcons = (selections: IconSelection[], bg = false) => {
  if (selections.length === 0) {
    return '';
  }

  const classes = ['review-selection__icon'];
  if (bg) {
    classes.push('review-selection__icon--bg');
  }

  const selectionList = selections.map((selection) => {
    return `
    <li>
      <img src="${selection.icon}" alt="${selection.name} icon" class="${classes.join(' ')}">
    </li>`;
  });
  const html = '<ul>' + selectionList.join('') + '</ul>';
  return html;
};

/** Get the icon src attributes from the DOM, based on the selection values */
const buildIconSelections = (form: HTMLFormElement, field: string) => {
  const formData = new FormData(form);
  const selections = formData.getAll(field);
  const icons: IconSelection[] = [];
  selections.forEach((value) => {
    const icon = form.querySelector<HTMLImageElement>(`input[name="${field}"][value="${value}"] + label img`);
    // TODO: Add nice name here as well?
    if (icon) {
      icons.push({
        name: value as string,
        icon: icon.src,
      });
    }
  });
  return icons;
};

/**
 * Update the review step with the form selections
 * @param form The ring assistant form
 */
const updateReviewStep = (form: HTMLFormElement) => {
  // Check if we're on the review step, and only update if so
  const reviewStep = form.querySelector('.review-selections-output')?.closest<HTMLElement>('.step');
  // Also show/hide the clear selection button
  const clearSelectionButton = form.querySelector<HTMLButtonElement>('.js-clear-step');

  if (reviewStep?.style?.display === 'none') {
    clearSelectionButton?.classList.remove('invisible');
    return;
  }

  clearSelectionButton?.classList.add('invisible');

  const formData = new FormData(form);

  // Origin
  const originSelectionsEl = form.querySelector('.js-single-selection[data-field="origin"]');
  if (originSelectionsEl) {
    const origin = formData.getAll('origin');
    const originValue = origin.includes('lab-grown')
      ? t('forms:ringAssistant.labGrown')
      : t('forms:ringAssistant.natural');

    // Build text display
    const selectionTextEl = originSelectionsEl.querySelector('.review-selection__col--text') as HTMLElement;
    // Pass empty array if no selection
    const originSelection = buildSelectionDisplayText(origin.length > 0 ? [originValue] : []);
    selectionTextEl.innerHTML = originSelection;

    // Build icons display
    const icons = buildIconSelections(form, 'origin');
    const selectionIconsEl = originSelectionsEl.querySelector('.review-selection__col--icons') as HTMLElement;
    const originIcons = buildSelectionDisplayIcons(icons);
    selectionIconsEl.innerHTML = originIcons;

    // Update accordion panel height
    const panel = originSelectionsEl.querySelector('.js-accordion-panel') as HTMLElement;
    if (panel.classList.contains('open')) {
      panel.style.maxHeight = `${panel.scrollHeight}px`;
    }
  }

  // Shape
  const shapeSelectionEl = form.querySelector('.js-single-selection[data-field="shape"]');
  if (shapeSelectionEl) {
    const gemstoneShape = formData.getAll('shape');

    // This gets the translated versions from the labels
    const gemstoneShapeLabelValues = gemstoneShape.map(
      (value) => getLabelValueFromInputValue(form, 'shape', value.toString())
    );

    const selectionTextEl = shapeSelectionEl.querySelector('.review-selection__col--text') as HTMLElement;
    const shapeSelection = buildSelectionDisplayText(gemstoneShapeLabelValues);
    selectionTextEl.innerHTML = shapeSelection;

    const selectionIconsEl = shapeSelectionEl.querySelector('.review-selection__col--icons') as HTMLElement;
    const icons = buildIconSelections(form, 'shape');
    const shapeIcons = buildSelectionDisplayIcons(icons);
    selectionIconsEl.innerHTML = shapeIcons;

    const panel = shapeSelectionEl.querySelector('.js-accordion-panel') as HTMLElement;
    if (panel.classList.contains('open')) {
      panel.style.maxHeight = `${panel.scrollHeight}px`;
    }
  }

  // Style
  const styleSelectionEl = form.querySelector('.js-single-selection[data-field="style"]');
  if (styleSelectionEl) {
    const style = formData.getAll('style');

    // This gets the translated versions from the labels
    const styleLabelValues = style.map(
      (value) => getLabelValueFromInputValue(form, 'style', value.toString())
    );

    const selectionTextEl = styleSelectionEl.querySelector('.review-selection__col--text') as HTMLElement;
    const styleSelection = buildSelectionDisplayText(styleLabelValues);
    selectionTextEl.innerHTML = styleSelection;

    const selectionIconsEl = styleSelectionEl.querySelector('.review-selection__col--icons') as HTMLElement;
    const icons = buildIconSelections(form, 'style');
    const styleIcons = buildSelectionDisplayIcons(icons);
    selectionIconsEl.innerHTML = styleIcons;

    const panel = styleSelectionEl.querySelector('.js-accordion-panel') as HTMLElement;
    if (panel.classList.contains('open')) {
      panel.style.maxHeight = `${panel.scrollHeight}px`;
    }
  }

  // Material
  const materialSelectionEl = form.querySelector('.js-single-selection[data-field="material-colour"]');
  if (materialSelectionEl) {
    const materialColour = formData.getAll('material-colour');

    // This gets the translated versions from the labels
    const materialColourLabelValues = materialColour.map(
      (value) => getLabelValueFromInputValue(form, 'material-colour', value.toString())
    );

    const selectionTextEl = materialSelectionEl.querySelector('.review-selection__col--text') as HTMLElement;
    const materialSelection = buildSelectionDisplayText(materialColourLabelValues);
    selectionTextEl.innerHTML = materialSelection;

    const selectionIconsEl = materialSelectionEl.querySelector('.review-selection__col--icons') as HTMLElement;
    const icons = buildIconSelections(form, 'material-colour');
    const materialIcons = buildSelectionDisplayIcons(icons, true);
    selectionIconsEl.innerHTML = materialIcons;

    const panel = materialSelectionEl.querySelector('.js-accordion-panel') as HTMLElement;
    if (panel.classList.contains('open')) {
      panel.style.maxHeight = `${panel.scrollHeight}px`;
    }
  }

  // Weight
  const weightSelectionEl = form.querySelector('.js-single-selection[data-field="weight"]');
  if (weightSelectionEl) {
    const weightMin = formData.get('weight_min') as string;
    const weightMax = formData.get('weight_max') as string;
    const selectionTextEl = weightSelectionEl.querySelector('.review-selection__col--text') as HTMLElement;
    const weightSelection = buildSelectionDisplayText([`${weightMin} ct. - ${weightMax} ct.`]);
    selectionTextEl.innerHTML = weightSelection;

    const panel = weightSelectionEl.querySelector('.js-accordion-panel') as HTMLElement;
    if (panel.classList.contains('open')) {
      panel.style.maxHeight = `${panel.scrollHeight}px`;
    }
  }

  // Price
  const priceSelectionEl = form.querySelector('.js-single-selection[data-field="budget"]');
  if (priceSelectionEl) {
    const priceMin = formData.get('budget_min') as string;
    const priceMax = formData.get('budget_max') as string;
    const priceSelection = buildSelectionDisplayText([`$${priceMin} - $${priceMax}`]);
    const selectionTextEl = priceSelectionEl.querySelector('.review-selection__col--text') as HTMLElement;
    selectionTextEl.innerHTML = priceSelection;

    const panel = priceSelectionEl.querySelector('.js-accordion-panel') as HTMLElement;
    if (panel.classList.contains('open')) {
      panel.style.maxHeight = `${panel.scrollHeight}px`;
    }
  }
};

const submitForm = async (form: HTMLFormElement) => {
  const algoliaOptions = buildAlgoliaOptions(form);
  console.debug('algoliaOptions', algoliaOptions);

  const redirectUrl = stardust.route(
    `${getPageLang()}.contentful.slug`,
    { '*': ['collections', 'engagement-rings'] },
    // NOTE: This seems to work with + instead of %20 because adonis translates it to a space in its qs parsing anyways
    // And Algolia instantsearch doesn't seem to care which we use either.
    { qs: algoliaOptions },
  );
  // console.debug('Redirect URL:', redirectUrl);

  const searchUrl = stardust.route('api.shop.search', undefined, {
    qs: {
      ...algoliaOptions,
      lang: getPageLang(),
    },
  });
  // console.debug('Search URL:', searchUrl);

  const response = await fetch(searchUrl);

  if (!response.ok) {
    console.error('Failed to fetch search results:', response);
    return;
  }

  const searchResults = await response.json();

  if (searchResults.hits.length === 0) {
    console.error('No search results found');
    toggleNoResultsScreen(true);
    return;
  } else {
    window.location.href = redirectUrl;
  }
};

document.addEventListener('DOMContentLoaded', async () => {
  const ringAssistantForm = document.querySelector<HTMLFormElement>('#ring-assistant-form form');
  if (!ringAssistantForm) {
    return;
  }

  // Load the namespaces we need
  t = await i18nextInstance;
  await i18next.loadNamespaces(['common', 'forms', 'algolia']);

  new MultiStepForm({
    form: ringAssistantForm,
    submitFn: async () => {
      await submitForm(ringAssistantForm);
    },
    afterStepFn: () => updateReviewStep(ringAssistantForm),
    startTab: 0,
    translations: {
      previous: t('common:back'),
      next: t('common:next'),
      submit: t('forms:ringAssistant.finish'),
      clear: t('forms:clear'),
    },
  });

  const dualHandleRanges = document.querySelectorAll<HTMLElement>('.js-dual-handle-range');
  dualHandleRanges.forEach((range) => {
    new DualHandleRange(range);
  });

  // Need to hide the "no results" screen when going back in the form
  const hideEndScreenButtons = document.querySelectorAll<HTMLButtonElement>('.js-hide-end-screen');
  hideEndScreenButtons.forEach((button) => {
    button.addEventListener('click', () => {
      toggleNoResultsScreen(false);
    });
  });

  // ============================================================
  // Code to auto-open the Ring Assistant modal.
  // If ring-assistant is present, will open unless finds cookie
  // If force-ring-assistant is present, will open regardless
  // ============================================================
  const params = new URLSearchParams(window.location.search);
  const ringAssistantParam = params.get('ring-assistant');
  const forceRingAssistantParam = params.get('force-ring-assistant');

  // Skip if no URL param
  if (!ringAssistantParam && !forceRingAssistantParam) {
    // console.debug('Ring Assistant not enabled');
    return;
  }

  // Skip if already dismissed, but allow force param to override
  const ringAssistantCookie = getCookie('ring-assistant-dismissed');
  if (ringAssistantCookie && !forceRingAssistantParam) {
    // console.debug('Ring Assistant dismissed');
    return;
  }

  window.openModal('ring-assistant');
  // Immediately set cookie after showing the modal, for use next time
  setCookie('ring-assistant-dismissed', 'true', 365);
});
